export const CART_ID_KEY = 'cart-id';

let cachedAvailability: boolean | null = null;

const isSessionStorageAvailable = (): boolean => {
  if (cachedAvailability !== null) return cachedAvailability;
  if (typeof window === 'undefined' || !window.sessionStorage) {
    // Don't cache — SSR false-negative would persist after hydration
    return false;
  }
  try {
    const probe = '__cartId_probe__';
    window.sessionStorage.setItem(probe, '1');
    window.sessionStorage.removeItem(probe);
    cachedAvailability = true;
  } catch {
    cachedAvailability = false;
  }
  return cachedAvailability;
};

// Fallback for older browsers that don't support crypto.randomUUID()
const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Gets the current cart ID or creates a new one.
 * Best-effort persistence in sessionStorage — if unavailable (Safari private mode,
 * quota exceeded, cookies disabled), returns a UUID in memory. Caller must propagate
 * the cartId on the URL to guarantee persistence via Redis session.
 */
export const getOrCreateCartId = (existingId?: string): string => {
  if (existingId) {
    try {
      if (isSessionStorageAvailable()) {
        sessionStorage.setItem(CART_ID_KEY, existingId);
      }
    } catch {
      // storage blocked — not critical, existingId still returned
    }
    return existingId;
  }

  if (isSessionStorageAvailable()) {
    const existing = sessionStorage.getItem(CART_ID_KEY);
    if (existing) return existing;
  }

  const cartId = generateUUID();
  try {
    if (isSessionStorageAvailable()) {
      sessionStorage.setItem(CART_ID_KEY, cartId);
    }
  } catch {
    // storage blocked — cartId still returned; caller should propagate via URL
  }
  return cartId;
};

/** Gets the current cart ID without creating one. Returns null if not available. */
export const getCartId = (): string | null => {
  if (!isSessionStorageAvailable()) {
    return null;
  }

  return sessionStorage.getItem(CART_ID_KEY);
};

/**
 * Forces creation of a new cart ID, replacing any existing one.
 * Best-effort persistence — returns a UUID even if sessionStorage is blocked.
 */
export const createNewCartId = (): string => {
  const cartId = generateUUID();
  try {
    if (isSessionStorageAvailable()) {
      sessionStorage.setItem(CART_ID_KEY, cartId);
    }
  } catch {
    // storage blocked — cartId still returned
  }
  return cartId;
};

export const clearCartId = (): void => {
  if (!isSessionStorageAvailable()) {
    return;
  }

  sessionStorage.removeItem(CART_ID_KEY);
};

export const hasCart = (): boolean => {
  if (!isSessionStorageAvailable()) {
    return false;
  }

  return sessionStorage.getItem(CART_ID_KEY) !== null;
};
